/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-06-25
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Fournisseur de langue du site Tchad : francais, anglais, arabe.
 *
 * Contrairement au site UK, les traductions ne sont pas chargees par le reseau
 * mais importees depuis `mockData.js` : elles font partie du bundle. Ce site
 * n'a donc ni ecran de chargement ni repli a prevoir — en contrepartie les
 * traductions pesent dans le poids du bundle, ce qui compte sur une connexion
 * modeste.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from '../mockData';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('fr');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('prenim-tchad-language');
    if (savedLanguage && ['fr', 'en', 'ar'].includes(savedLanguage)) {
      setLanguage(savedLanguage);
    }
  }, []);

  // `lang` et `dir` etaient poses par changeLanguage uniquement : au premier
  // chargement, le document restait en `lang="en"` meme en francais. La cesure
  // automatique, sur laquelle repose le texte justifie, s'appuie sur `lang` —
  // sans lui, les paragraphes justifies se creusent de blancs.
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('prenim-tchad-language', lang);
  };

  const t = translations[language] || translations.fr;

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
